/**
 * accountStore — localStorage-backed persistence for the multi-account switcher.
 *
 * Shape:
 *   {
 *     activeUserId: string | null,
 *     accounts: [
 *       { id, name, email, role, avatarUrl, refreshToken, savedAt }
 *     ]
 *   }
 *
 * The refresh token in each entry is what lets us "one-click switch" without
 * asking for a password. The server-side RefreshToken row backs it, so we can
 * revoke individual accounts (Remove Account, Log Out Current) or all of them
 * (Log Out All) even from a different device.
 *
 * Access tokens live in `localStorage['token']` (unchanged from single-account
 * behavior) so api.js / socket.js keep working without changes.
 */

const KEY = 'seo_taskops_accounts';
const ACCESS_TOKEN_KEY = 'token';

function readState() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { activeUserId: null, accounts: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.accounts)) return { activeUserId: null, accounts: [] };
    return { activeUserId: parsed.activeUserId || null, accounts: parsed.accounts };
  } catch {
    return { activeUserId: null, accounts: [] };
  }
}

function writeState(state) {
  try {
    localStorage.setItem(KEY, JSON.stringify({
      activeUserId: state.activeUserId || null,
      accounts: state.accounts || [],
    }));
  } catch {
    // storage full / private mode — degrade to single-session behavior
  }
}

function getState() {
  return readState();
}

function listAccounts() {
  return readState().accounts;
}

function getActiveAccount() {
  const s = readState();
  return s.accounts.find((a) => a.id === s.activeUserId) || null;
}

function getAccount(userId) {
  return readState().accounts.find((a) => a.id === userId) || null;
}

/**
 * Insert-or-update an account entry after a fresh login / refresh, and mark
 * it as the active user. `account` is the tile-shape object the backend
 * returns (`{ id, name, email, role }`). `refreshToken` is the raw
 * refresh token that lets us one-click switch to this account later.
 */
function upsertAccount(account, refreshToken) {
  const s = readState();
  const entry = {
    id: String(account.id),
    name: account.name || '',
    email: account.email || '',
    role: account.role || '',
    refreshToken: refreshToken || null,
    savedAt: new Date().toISOString(),
  };
  const idx = s.accounts.findIndex((a) => a.id === entry.id);
  if (idx === -1) s.accounts.push(entry);
  else s.accounts[idx] = { ...s.accounts[idx], ...entry };
  s.activeUserId = entry.id;
  writeState(s);
  return entry;
}

/** Called after /refresh rotates the refresh token — store the new one. */
function updateRefreshToken(userId, refreshToken) {
  const s = readState();
  const idx = s.accounts.findIndex((a) => a.id === String(userId));
  if (idx !== -1) {
    s.accounts[idx].refreshToken = refreshToken || null;
    s.accounts[idx].savedAt = new Date().toISOString();
    writeState(s);
  }
}

function setActive(userId) {
  const s = readState();
  if (s.accounts.some((a) => a.id === String(userId))) {
    s.activeUserId = String(userId);
    writeState(s);
  }
}

/** Remove one account (without touching the server — caller handles that). */
function removeAccount(userId) {
  const s = readState();
  s.accounts = s.accounts.filter((a) => a.id !== String(userId));
  if (s.activeUserId === String(userId)) {
    // Fall back to another saved account if there is one.
    s.activeUserId = s.accounts[0]?.id || null;
  }
  writeState(s);
  return s.activeUserId ? s.accounts.find((a) => a.id === s.activeUserId) : null;
}

function clearAll() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  try { localStorage.removeItem(ACCESS_TOKEN_KEY); } catch { /* ignore */ }
}

function setAccessToken(token) {
  if (token) localStorage.setItem(ACCESS_TOKEN_KEY, token);
  else localStorage.removeItem(ACCESS_TOKEN_KEY);
}

function getAccessToken() {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export default {
  getState,
  listAccounts,
  getActiveAccount,
  getAccount,
  upsertAccount,
  updateRefreshToken,
  setActive,
  removeAccount,
  clearAll,
  setAccessToken,
  getAccessToken,
};
